/**
 * Painel de preview WhatsApp em tempo real
 * Simula a aparência da mensagem no WhatsApp
 */

import { Image, Video, FileIcon, ExternalLink, Phone, MessageSquare, CreditCard, Copy, QrCode, ImageIcon, VideoIcon, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HeaderFormat, TemplateButton } from './types';
import { useState, useEffect } from 'react';

interface TemplatePreviewPanelProps {
  headerFormat: HeaderFormat;
  headerText: string;
  headerMediaUrl?: string;
  bodyText: string;
  footerText: string;
  buttons: TemplateButton[];
  exampleValues?: Record<string, string>;
}

/**
 * Formata texto com variáveis destacadas
 */
function formatTextWithVariables(text: string): React.ReactNode {
  if (!text) return null;
  
  // Substitui {{n}} por spans destacados
  const parts = text.split(/(\{\{\d+\}\})/g);
  
  return parts.map((part, index) => {
    if (/\{\{\d+\}\}/.test(part)) {
      return (
        <span 
          key={index} 
          className="bg-primary/20 text-primary px-1 rounded text-xs font-mono"
        >
          {part}
        </span>
      );
    }
    return part;
  });
}

/**
 * Componente de preview de mídia com thumbnail real ou placeholder elegante
 */
function MediaPreview({ format, mediaUrl }: { format: HeaderFormat; mediaUrl?: string }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Resetar estado quando URL muda
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [mediaUrl]);
  
  // Se tem URL válida e é imagem, tenta carregar
  if (format === 'IMAGE' && mediaUrl && !imageError) {
    return (
      <div className="relative w-full aspect-video bg-muted/50 overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <ImageIcon className="h-8 w-8 animate-pulse" />
              <span className="text-xs">Carregando...</span>
            </div>
          </div>
        )}
        <img 
          src={mediaUrl}
          alt="Preview"
          className={cn(
            "w-full h-full object-cover transition-opacity",
            imageLoaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }
  
  // Para vídeo com URL, renderiza player real com controles
  if (format === 'VIDEO' && mediaUrl) {
    return (
      <div className="relative w-full aspect-video bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden">
        <video
          src={mediaUrl}
          controls
          preload="metadata"
          className="w-full h-full object-contain"
        />
      </div>
    );
  }
  
  // Para documento com URL, mostra preview de documento
  if (format === 'DOCUMENT' && mediaUrl) {
    const fileName = mediaUrl.split('/').pop()?.split('?')[0] || 'documento.pdf';
    return (
      <div className="p-4 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 flex items-center gap-3">
        <div className="w-12 h-14 bg-white dark:bg-slate-800 rounded shadow-sm flex items-center justify-center border">
          <FileText className="h-6 w-6 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{fileName}</p>
          <p className="text-xs text-muted-foreground">PDF Document</p>
        </div>
      </div>
    );
  }
  
  // Placeholder padrão (sem URL ou erro de carregamento)
  const getPlaceholderContent = () => {
    switch (format) {
      case 'IMAGE':
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-blue-500" />
            </div>
            <span className="text-xs text-muted-foreground mt-2">Imagem</span>
            <span className="text-[10px] text-muted-foreground">Adicione uma URL para preview</span>
          </>
        );
      case 'VIDEO':
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <VideoIcon className="h-8 w-8 text-purple-500" />
            </div>
            <span className="text-xs text-muted-foreground mt-2">Vídeo</span>
            <span className="text-[10px] text-muted-foreground">Adicione uma URL para preview</span>
          </>
        );
      case 'DOCUMENT':
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <FileText className="h-8 w-8 text-orange-500" />
            </div>
            <span className="text-xs text-muted-foreground mt-2">Documento</span>
            <span className="text-[10px] text-muted-foreground">Adicione uma URL para preview</span>
          </>
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="p-6 bg-gradient-to-br from-muted/30 to-muted/60 flex flex-col items-center justify-center min-h-[120px]">
      {getPlaceholderContent()}
    </div>
  );
}

/**
 * Ícone do tipo de botão
 */
function ButtonTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'URL':
      return <ExternalLink className="h-3.5 w-3.5" />;
    case 'PHONE_NUMBER':
      return <Phone className="h-3.5 w-3.5" />;
    case 'COPY_CODE':
      return <Copy className="h-3.5 w-3.5" />;
    case 'ORDER_DETAILS':
      return <CreditCard className="h-3.5 w-3.5" />;
    default:
      return <MessageSquare className="h-3.5 w-3.5" />;
  }
}

export function TemplatePreviewPanel({
  headerFormat,
  headerText,
  headerMediaUrl,
  bodyText,
  footerText,
  buttons,
}: TemplatePreviewPanelProps) {
  const hasHeader = headerFormat !== 'NONE';
  const hasBody = bodyText.trim().length > 0;
  const hasFooter = footerText.trim().length > 0;
  const hasButtons = buttons.length > 0;
  
  return (
    <aside className="hidden lg:flex flex-col border-l bg-muted/30">
      {/* Header do painel */}
      <div className="flex-shrink-0 px-4 py-3 border-b bg-background">
        <h3 className="text-sm font-medium">Preview</h3>
        <p className="text-xs text-muted-foreground">
          Visualização em tempo real
        </p>
      </div>
      
      {/* Área do preview - fundo WhatsApp */}
      <div 
        className="flex-1 p-4 overflow-y-auto"
        style={{ backgroundColor: '#e5ddd5' }}
      >
        <div className="max-w-[260px] mx-auto">
          {/* Balão de mensagem - cores fixas para garantir legibilidade em dark mode */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden text-slate-900">
            
            {/* Header */}
            {hasHeader && (
              <div className="border-b border-slate-200 overflow-hidden">
                {headerFormat === 'TEXT' ? (
                  <div className="px-3 py-2 font-semibold text-sm text-slate-900">
                    {headerText ? formatTextWithVariables(headerText) : (
                      <span className="text-slate-400 italic">
                        Digite o texto do header...
                      </span>
                    )}
                  </div>
                ) : (
                  <MediaPreview format={headerFormat} mediaUrl={headerMediaUrl} />
                )}
              </div>
            )}
            
            {/* Body */}
            <div className="px-3 py-2 text-sm whitespace-pre-wrap min-h-[60px] max-h-[300px] overflow-y-auto text-slate-900">
              {hasBody ? (
                formatTextWithVariables(bodyText)
              ) : (
                <span className="text-slate-400 italic">
                  Digite o corpo da mensagem...
                </span>
              )}
            </div>
            
            {/* Footer */}
            {hasFooter && (
              <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200">
                {footerText}
              </div>
            )}
            
            {/* Botões */}
            {hasButtons && (
              <div className="border-t">
                {buttons.filter(b => b.text.trim()).map((btn, index) => (
                  <div 
                    key={index} 
                    className={cn(
                      "w-full px-3 py-2.5 text-sm font-medium",
                      "flex items-center justify-center gap-2",
                      index > 0 && "border-t",
                      btn.type === 'ORDER_DETAILS' 
                        ? "text-green-600" 
                        : "text-blue-500"
                    )}
                  >
                    <ButtonTypeIcon type={btn.type} />
                    {btn.text || 'Texto do botão'}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Timestamp */}
          <div className="text-right mt-1">
            <span className="text-[10px] text-slate-500">
              {new Date().toLocaleTimeString('pt-BR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
          
          {/* Indicador de template vazio */}
          {!hasBody && !hasHeader && !hasButtons && (
            <div className="mt-4 text-center">
              <p className="text-xs text-slate-500">
                Preencha os campos para ver o preview
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
